import { ChangeDetectionStrategy, Component, computed, effect, ElementRef, HostBinding, inject, NgZone, untracked, viewChild } from '@angular/core';
import { ContextService } from '@maximizer/core/shared/data-access';
import { EmbeddedComponent } from '@maximizer/core/shared/ui/components/base';
import { NotificationComponent } from '@maximizer/core/shared/ui/components/notification';
import { NotificationBannerComponent } from '@maximizer/core/shared/ui/components/notification-banner';
import { ActionEventType, ShowDialogArgs } from '@maximizer/core/shared/ui/config';
import { TranslatePipe } from '@ngx-translate/core';
import { KENDO_BUTTON } from '@progress/kendo-angular-buttons';
import { KENDO_TABSTRIP, SelectEvent } from '@progress/kendo-angular-layout';
import { AiHubEventTracker, EventAction, EventJourney } from '../classes/event-tracker';
import { AiHubStore } from './ai-hub.store';
import { GeneralTabComponent } from './general-tab/general-tab.component';
import { AiHubTab, ToolApp } from './models/ai-hub.models';
import { PreferencesTabComponent } from './preferences-tab/preferences-tab.component';
import { ToolsTabComponent } from './tools-tab/tools-tab.component';

interface HubTab {
  id: AiHubTab;
  titleKey: string;
}

const BASE_TABS: readonly HubTab[] = [
  { id: 'general', titleKey: 'aiHub.tabs.general' },
  { id: 'tools', titleKey: 'aiHub.tabs.tools' },
];

const PREFERENCES_TAB: HubTab = { id: 'preferences', titleKey: 'aiHub.tabs.preferences' };

@Component({
  selector: 'ai-hub-v2',
  templateUrl: './ai-hub-v2.component.html',
  styleUrl: './ai-hub-v2.component.scss',
  providers: [AiHubEventTracker, AiHubStore],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    GeneralTabComponent,
    KENDO_BUTTON,
    KENDO_TABSTRIP,
    NotificationBannerComponent,
    NotificationComponent,
    PreferencesTabComponent,
    ToolsTabComponent,
    TranslatePipe,
  ],
})
export class AiHubV2Component extends EmbeddedComponent {
  @HostBinding('class') hostClasses = 'ai-ng';

  readonly store = inject(AiHubStore);
  private readonly tracker = inject(AiHubEventTracker);
  private readonly context = inject(ContextService);
  private readonly ngZone = inject(NgZone);
  private readonly saveErrorNotification = viewChild.required<NotificationComponent>('saveError');

  readonly tabs = computed(() => (this.store.preferencesEnabledByFlag() ? [...BASE_TABS, PREFERENCES_TAB] : BASE_TABS));
  readonly loadingGifUrl = computed(() => `${this.context.integration?.url ?? ''}/resources/ai-loading.gif`);

  constructor() {
    super(inject(ElementRef));
    effect(() => {
      if (this.store.saveErrorCount() > 0) {
        untracked(() => this.saveErrorNotification().show());
      }
    });
  }

  override doAction(name: string, args?: unknown): void {
    this.ngZone.run(() => {
      if (name === ActionEventType.Initialize) {
        if (args) this.initialize();
      } else {
        super.doAction(name, args);
      }
    });
  }

  onTabSelect(event: SelectEvent): void {
    this.store.selectTab(this.tabs()[event.index].id);
  }

  openFeatureApp(app: ToolApp): void {
    this.actionCompleted.emit(new ShowDialogArgs(app.tag, ''));
  }

  override ngOnDestroy(): void {
    this.tracker.trackEvent(EventJourney.Action, EventAction.Closed);
    super.ngOnDestroy();
  }

  private initialize(): void {
    this.tracker.trackEvent(EventJourney.Action, EventAction.Opened);
    this.store.initialize();
  }
}
